/**
 * Built-in pipeline and playbook defaults.
 *
 * These are the seed values written into `skidaCrmSettings` by `npm run seed`,
 * and the fallback the UI renders before that document exists. Once an admin
 * edits anything in Admin → Process & template settings, Firestore is the source
 * of truth and these are no longer consulted for that field.
 *
 * Copy is final-form Norwegian/English from the design handoff — treat edits
 * here as product copy changes, not code changes.
 */
// Relative rather than aliased: scripts/seedSettings.ts imports this module
// directly under tsx, which does not resolve the `@/` alias.
import type { EmailTemplate, PipelineOutcome, PipelineStage } from '../../types/crm'

/** Palette offered by the colour swatch in the stage editor. */
export const COLOR_PRESETS: { color: string; bg: string }[] = [
  { color: '#AEB1B4', bg: '#f1f2f3' },
  { color: '#5b8fd6', bg: '#eaf1fc' },
  { color: '#7c3fc4', bg: '#f2e9fb' },
  { color: '#0f9b8e', bg: '#e3f6f3' },
  { color: '#e8a52f', bg: '#fff3d8' },
  { color: '#c2477a', bg: '#fbe7ef' },
  { color: '#3456DA', bg: '#eaefff' },
]

/**
 * `builtin: true` marks stages the Cloud Functions reference by key
 * (`crmFirstLoginWatcher` advances leads to `proposal`, `crmSeatSync` to `won`).
 * The admin UI blocks deleting these.
 */
export const DEFAULT_STAGES: PipelineStage[] = [
  {
    key: 'unverified',
    label: 'Unverified',
    color: '#c2477a',
    bg: '#fbe7ef',
    builtin: true,
    hidden: true,
  },
  {
    // Held back like `unverified`, but for the opposite reason: not "we know
    // too little", but "we know something that means we must not call".
    // A company that has just lost people in an avalanche must never surface in
    // a list a rep is working down, and the scoring spec excludes them outright.
    key: 'do-not-contact',
    label: 'Do not contact',
    color: '#9e2020',
    bg: '#fdeaea',
    builtin: true,
    hidden: true,
  },
  {
    // Where the Delete button on an unverified lead sends it. Nothing is ever
    // destroyed — a lead somebody decided against is evidence of that decision,
    // and the next import would otherwise recreate it as if it were new.
    key: 'archived',
    label: 'Archived',
    color: '#7c8798',
    bg: '#eef2f8',
    builtin: true,
    hidden: true,
  },
  { key: 'prospect', label: 'Prospect / Lead', color: '#AEB1B4', bg: '#f1f2f3', builtin: true },
  { key: 'contacted', label: 'Contacted', color: '#5b8fd6', bg: '#eaf1fc', builtin: true },
  { key: 'proposal', label: 'Proposal / Downloaded', color: '#7c3fc4', bg: '#f2e9fb', builtin: true },
]

export const DEFAULT_OUTCOMES: PipelineOutcome[] = [
  { key: 'won', label: 'Won', color: '#3aa52f', bg: '#eaf7e7' },
  { key: 'lost', label: 'Lost', color: '#e63333', bg: '#fdeaea' },
]

/**
 * The outreach playbook lives in its own module — it is product copy, and it
 * had grown to two thirds of this file. Re-exported so every existing import
 * keeps working.
 */
export { DEFAULT_TEMPLATES } from './templates'

export const EMPTY_TEMPLATE: EmailTemplate = {
  title: 'Send outreach',
  guidance: 'No template yet for this stage. Add one in Admin → Email templates.',
  subjectNo: '',
  bodyNo: '',
  subjectEn: '',
  bodyEn: '',
}
